/**
 * /impact - who and what the computed flood reaches.
 *
 * Every figure on this page is read from GET /api/runs/{id}/impact, which
 * computes it from the run's own depth, velocity, arrival-time and duration
 * rasters. Nothing here is typed in.
 *
 * The settlement records are points, so an areal fraction needs an area to be
 * a fraction of: that area is a disc of the radius shown in the header, the
 * same for every settlement, and it is what the inundated fraction, the
 * inundated area and the population at risk are all measured inside.
 */

import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import { api } from '../api/client'
import { useAppStore } from '../store/useAppStore'
import type { Run } from '../api/types'
import { Marker } from 'react-map-gl/maplibre'
import type { ExpressionSpecification, LayerSpecification, SourceSpecification } from 'maplibre-gl'
import { BaseMap, type Bounds } from '../components/BaseMap'
import { HAZARD } from '../components/Legend'
import { C, fs } from '../design/tokens'
import { SiteExposure, type ExposurePayload } from './impact/SiteExposure'
import './impact.css'

interface SettlementImpact {
  id: number
  name: string
  type: string | null
  lat: number
  lon: number
  district: string | null
  population: number | null
  population_source: string | null
  distance_from_dam_km: number | null
  arrival_time_min: number | null
  max_depth_m: number | null
  max_velocity_ms: number | null
  duration_h: number | null
  hazard_class: number | null
  hazard_label: string | null
  area_fraction_inundated: number
  inundated_area_km2: number
  population_at_risk: number | null
}

interface ImpactPayload {
  run_id: string
  radius_km: number
  cell_size_m: number
  wet_threshold_m: number
  footprint: {
    inundated_area_km2: number
    wet_cells: number
    max_depth_m: number | null
    max_velocity_ms: number | null
    duration_h_max: number | null
  }
  totals: {
    population_in_footprint: number
    inundated_area_km2: number
    shortest_arrival_min: number | null
    settlements_affected: number
    settlements_total: number
  }
  settlements: SettlementImpact[]
  infrastructure: {
    site_id: string | null
    categories: InfraCategory[]
    attribution?: string
    fetched_utc?: string | null
  }
}

interface InfraCategory {
  key: string
  category: string
  detail: string
  value: number
  unit: string
  names?: string[]
}

type InfraFeature = {
  properties: { name: string | null; class: string | null; in_footprint: boolean }
  geometry: { type: string; coordinates: number[] | number[][] | number[][][] }
}

interface InfraMap {
  site_id: string | null
  layers: Record<string, { features: InfraFeature[] }>
}

/** Infrastructure on the footprint map: lines and square-cornered marks, so nothing reads as a settlement circle. */
const INFRA_IN = C.mapRiver
const INFRA_OUT = C.mapInfraOut
const FACILITY_LEGEND: [string, string][] = [
  ['substations', 'Substation'],
  ['hospitals', 'Hospital'],
  ['schools', 'School'],
  ['stations', 'Railway station'],
]

function FacilityMark({ layer, colour }: { layer: string; colour: string }) {
  if (layer === 'substations') return <rect x="-4" y="-4" width="8" height="8" fill="none" stroke={colour} strokeWidth="1.5" />
  if (layer === 'hospitals') return <path d="M-4 0H4M0 -4V4" stroke={colour} strokeWidth="2" />
  if (layer === 'schools') return <path d="M0 -5L5 4H-5Z" fill="none" stroke={colour} strokeWidth="1.5" />
  return <path d="M0 -5L5 0L0 5L-5 0Z" fill="none" stroke={colour} strokeWidth="1.5" />
}

type GeoJson = {
  features: { geometry: { type: string; coordinates: number[][][] | number[][][][] } }[]
}

/** Hazard class colours: the one definition in backend/render/colormaps.py. */
const hazardColour = (c: number | null) => (c ? HAZARD[c] : 'var(--ink-text-faint)')

const RADII = [1, 2, 5, 10] as const

type SortKey =
  | 'name'
  | 'distance_from_dam_km'
  | 'population'
  | 'arrival_time_min'
  | 'max_depth_m'
  | 'max_velocity_ms'
  | 'hazard_class'

const COLUMNS: [SortKey, string, boolean][] = [
  ['name', 'Settlement', false],
  ['distance_from_dam_km', 'From dam (km)', true],
  ['population', 'Population', true],
  ['arrival_time_min', 'Arrival (h)', true],
  ['max_depth_m', 'Depth (m)', true],
  ['max_velocity_ms', 'Vel (m/s)', true],
  ['hazard_class', 'Hazard', false],
]

const fmt = (v: number, d = 2) =>
  v.toLocaleString('en-IN', { minimumFractionDigits: d, maximumFractionDigits: d })
const int = (v: number) => v.toLocaleString('en-IN')

/** Minutes as T+HH:MM, the same clock the timeline and the alerts use. */
function clock(minutes: number): string {
  const total = Math.max(0, Math.round(minutes))
  return `T+${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`
}

/**
 * The rendered pixel size of an element. Both drawings below are built at 1:1
 * against it rather than scaled by the SVG viewBox, because a viewBox that
 * shrinks takes every label with it - a 12 px tick in a drawing squeezed to
 * 60% renders at 7 px, under the floor the type scale sets.
 */
function useBox() {
  const [box, setBox] = useState({ w: 0, h: 0 })
  const [el, ref] = useState<HTMLDivElement | null>(null)
  useEffect(() => {
    if (!el) return undefined
    const measure = () =>
      setBox((b) => (b.w === el.clientWidth && b.h === el.clientHeight ? b : { w: el.clientWidth, h: el.clientHeight }))
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    measure()
    return () => observer.disconnect()
  }, [el])
  return [box, ref] as const
}

export default function Impact() {
  const [runId, setRunId] = useState<string | null>(null)
  const [radiusKm, setRadiusKm] = useState<(typeof RADII)[number]>(2)
  const [sort, setSort] = useState<{ key: SortKey; desc: boolean }>({ key: 'arrival_time_min', desc: false })
  const [chartBox, chartRef] = useBox()

  const runs = useQuery<Run[]>({ queryKey: ['runs'], queryFn: api.runs })
  const activeSiteId = useAppStore((s) => s.activeSiteId)
  const doneRuns = useMemo(
    () => (runs.data ?? []).filter((r) => r.status === 'done' && r.solver !== 'dualsphysics' && r.site_id === activeSiteId),
    [runs.data, activeSiteId],
  )
  const chosen = runId && doneRuns.some((r) => r.id === runId) ? runId : null
  const activeRun = chosen ?? doneRuns.find((r) => r.id === 'hecras_corrected_A')?.id ?? doneRuns[0]?.id ?? null

  const impact = useQuery<ImpactPayload>({
    queryKey: ['impact', activeRun, radiusKm],
    queryFn: () => api.raw(`/runs/${activeRun}/impact?radius_km=${radiusKm}`),
    enabled: Boolean(activeRun),
    placeholderData: (p) => p,
  })

  // The flash-flood site's exposure, from OSM layers fetched for it; unavailable (and unrendered) elsewhere.
  const exposure = useQuery<ExposurePayload>({
    queryKey: ['exposure', activeRun],
    queryFn: () => api.raw(`/runs/${activeRun}/exposure`),
    enabled: Boolean(activeRun),
    staleTime: Infinity,
  })

  const extent = useQuery<GeoJson>({
    queryKey: ['extent', activeRun],
    queryFn: () => api.raw(`/runs/${activeRun}/extent.geojson`),
    enabled: Boolean(activeRun),
    staleTime: Infinity,
  })

  const infraMap = useQuery<InfraMap>({
    queryKey: ['infrastructure-map', activeRun],
    queryFn: () => api.raw(`/runs/${activeRun}/infrastructure.geojson`),
    enabled: Boolean(activeRun),
    staleTime: Infinity,
  })

  const data = impact.data
  const hasPlaces = (data?.settlements.length ?? 0) > 0
  const rows = useMemo(() => {
    const list = [...(data?.settlements ?? [])]
    const { key, desc } = sort
    list.sort((a, b) => {
      const av = a[key]
      const bv = b[key]
      if (typeof av === 'string' && typeof bv === 'string') return desc ? bv.localeCompare(av) : av.localeCompare(bv)
      // Settlements the flood never reaches carry no value; they sort last
      // whichever way the column is pointing.
      const an = av === null ? Number.POSITIVE_INFINITY : Number(av)
      const bn = bv === null ? Number.POSITIVE_INFINITY : Number(bv)
      if (an === bn) return a.name.localeCompare(b.name)
      if (!Number.isFinite(an)) return 1
      if (!Number.isFinite(bn)) return -1
      return desc ? bn - an : an - bn
    })
    return list
  }, [data, sort])

  // ------------------------------------------------------------ map geometry

  const map = useMemo(() => {
    const places = data?.settlements ?? []
    const features = extent.data?.features ?? []
    if (!features.length && !places.length) return null

    let w = Infinity
    let s = Infinity
    let e = -Infinity
    let n = -Infinity
    const grow = (lo: number, la: number) => {
      if (lo < w) w = lo
      if (lo > e) e = lo
      if (la < s) s = la
      if (la > n) n = la
    }
    for (const f of features) {
      const g = f.geometry
      const groups = g.type === 'Polygon' ? [g.coordinates as number[][][]] : (g.coordinates as number[][][][])
      for (const poly of groups) for (const ring of poly) for (const [lo, la] of ring) grow(lo, la)
    }
    for (const place of places) grow(place.lon, place.lat)
    if (!Number.isFinite(w)) return null
    const bounds: Bounds = [w, s, e, n]

    const layers = infraMap.data?.layers ?? {}
    const lineFeatures = ['roads', 'railways', 'bridges'].flatMap((layer) =>
      (layers[layer]?.features ?? [])
        .filter((f) => f.geometry.type === 'LineString' || f.geometry.type === 'MultiLineString')
        .map((f) => ({ type: 'Feature' as const, geometry: f.geometry, properties: { layer, inside: f.properties.in_footprint } })),
    )
    const sources: Record<string, SourceSpecification> = {
      footprint: { type: 'geojson', data: { type: 'FeatureCollection', features: features as never } },
      infra: { type: 'geojson', data: { type: 'FeatureCollection', features: lineFeatures as never } },
    }
    const inside: ExpressionSpecification = ['get', 'inside']
    const mapLayers: LayerSpecification[] = [
      { id: 'footprint-fill', type: 'fill', source: 'footprint', paint: { 'fill-color': HAZARD[3], 'fill-opacity': 0.4 } },
      { id: 'footprint-line', type: 'line', source: 'footprint', paint: { 'line-color': HAZARD[2], 'line-width': 1 } },
      {
        id: 'infra-roads',
        type: 'line',
        source: 'infra',
        filter: ['==', ['get', 'layer'], 'roads'],
        paint: { 'line-color': ['case', inside, INFRA_IN, INFRA_OUT], 'line-width': ['case', inside, 1.5, 1] },
      },
      {
        id: 'infra-rail',
        type: 'line',
        source: 'infra',
        filter: ['==', ['get', 'layer'], 'railways'],
        paint: { 'line-color': ['case', inside, INFRA_IN, INFRA_OUT], 'line-width': ['case', inside, 2, 1.5], 'line-dasharray': [3, 2] },
      },
      {
        id: 'infra-bridges',
        type: 'line',
        source: 'infra',
        filter: ['all', ['==', ['get', 'layer'], 'bridges'], inside],
        paint: { 'line-color': C.text, 'line-width': 4 },
      },
    ]
    const facilities = FACILITY_LEGEND.flatMap(([layer]) =>
      (layers[layer]?.features ?? [])
        .filter((f) => f.geometry.type === 'Point')
        .map((f, i) => {
          const [lo, la] = f.geometry.coordinates as number[]
          return { key: `${layer}-${i}`, layer, lon: lo, lat: la, inside: f.properties.in_footprint, name: f.properties.name }
        }),
    )
    const present = new Set(
      Object.entries(layers)
        .filter(([, v]) => v.features.length)
        .map(([k]) => k),
    )
    return { bounds, sources, layers: mapLayers, facilities, present }
  }, [extent.data, data, infraMap.data])

  // ------------------------------------------------------------ arrival chart

  const chart = useMemo(() => {
    const reached = (data?.settlements ?? []).filter((p) => p.arrival_time_min !== null)
    if (!reached.length) return null
    reached.sort((a, b) => (a.arrival_time_min ?? 0) - (b.arrival_time_min ?? 0))
    const maxH = Math.max(...reached.map((p) => (p.arrival_time_min ?? 0) / 60))
    const step = [1, 2, 3, 6, 12, 24, 48].find((c) => maxH / c <= 5) ?? 96
    const top = Math.ceil(maxH / step) * step || step
    const ticks: number[] = []
    for (let t = 0; t <= top + 1e-9; t += step) ticks.push(t)
    return { reached, top, ticks }
  }, [data])

  const radiusLabel = data ? `${fmt(data.radius_km, data.radius_km % 1 ? 1 : 0)} km` : ''
  const totals = data?.totals

  return (
    <div className="instrument impact flex h-full min-h-0 flex-col overflow-y-auto" style={{ background: 'var(--ink-bg)' }}>
      {/* Header: run, footprint radius */}
      <header
        className="flex shrink-0 flex-wrap items-center gap-4 border-b px-5 py-3"
        style={{ borderColor: 'var(--ink-border)', background: 'var(--ink-surface)' }}
      >
        <h1 className="page-title" style={{ fontSize: 'var(--fs-heading)' }}>
          Impact
        </h1>
        <select
          className="ctl"
          aria-label="Run"
          value={activeRun ?? ''}
          onChange={(ev) => setRunId(ev.target.value)}
          style={{
            background: 'var(--ink-surface-raised)',
            border: '1px solid var(--ink-border)',
            borderRadius: 'var(--r-control)',
            color: 'var(--ink-text)',
            padding: '0 8px',
          }}
        >
          {doneRuns.map((r) => (
            <option key={r.id} value={r.id}>
              {r.solver} · {/^[0-9a-f]{8}-[0-9a-f]{4}-/.test(r.id) ? r.id.slice(0, 8) : r.id}
            </option>
          ))}
        </select>
        {!exposure.data?.available && (
        <div className="ml-auto flex items-center gap-2">
          <span className="section-label">Settlement footprint radius</span>
          {RADII.map((r) => (
            <button
              key={r}
              className="ctl num"
              data-active={radiusKm === r}
              onClick={() => setRadiusKm(r)}
              style={{ minWidth: 48 }}
            >
              {r} km
            </button>
          ))}
        </div>
        )}
      </header>

      {exposure.data?.available ? (
        <SiteExposure data={exposure.data} />
      ) : !data || exposure.isPending ? (
        <div className="grid flex-1 grid-cols-4 gap-4 p-5">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="skeleton h-24" />
          ))}
        </div>
      ) : (
        <div className="flex flex-1 flex-col gap-4 p-5">
          {/* TOP: metric tiles - the settlement ones only where the site has settlements */}
          <div className={`grid shrink-0 gap-4 ${hasPlaces ? 'grid-cols-4' : 'grid-cols-1 md:max-w-[25%]'}`}>
            {hasPlaces && (
            <div className="metric-tile">
              <span className="section-label">Population in footprint</span>
              <span className="key-figure">{int(totals!.population_in_footprint)}</span>
              <span className="metric-note">
                {totals!.settlements_affected} of {totals!.settlements_total} settlements, within {radiusLabel}
              </span>
            </div>
            )}
            <div className="metric-tile">
              <span className="section-label">Inundated area</span>
              <span className="key-figure">
                {fmt(totals!.inundated_area_km2, 1)}
                <span className="unit">km²</span>
              </span>
              <span className="metric-note">
                {int(data.footprint.wet_cells)} cells over {data.wet_threshold_m} m
              </span>
            </div>
            {hasPlaces && (
            <>
            {totals!.shortest_arrival_min !== null && (
            <div className="metric-tile">
              <span className="section-label">Shortest arrival</span>
              <span className="key-figure">
                {fmt(totals!.shortest_arrival_min / 60, 1)}
                <span className="unit">h</span>
              </span>
              <span className="metric-note">{clock(totals!.shortest_arrival_min)} after breach</span>
            </div>
            )}
            <div className="metric-tile">
              <span className="section-label">Settlements affected</span>
              <span className="key-figure">{totals!.settlements_affected}</span>
              <span className="metric-note">
                {data.footprint.max_depth_m === null ? '' : `peak depth ${fmt(data.footprint.max_depth_m, 1)} m`}
              </span>
            </div>
            </>
            )}
          </div>

          {/* MAP: its own full-width row, the page's centrepiece */}
            {/* CENTRE: footprint map */}
            <section className="panel flex flex-col" style={{ padding: 0 }}>
              <div className="panel-header">
                <span className="panel-title" style={{ fontSize: 'var(--fs-body)', fontWeight: 600 }}>
                  Flood footprint
                </span>
                <span className="section-label">marker area ∝ population at risk</span>
              </div>
              <div className="relative" style={{ height: 'clamp(460px, 64vh, 780px)' }} data-testid="impact-map">
                {map && (
                  <BaseMap
                    bounds={map.bounds}
                    runId={activeRun}
                    sources={map.sources}
                    layers={map.layers}
                    padding={40}
                    dragRotate={false}
                    maxPitch={0}
                  >
                    {map.facilities.map((m) => (
                      <Marker key={m.key} longitude={m.lon} latitude={m.lat} anchor="center" style={{ pointerEvents: 'none' }}>
                        <svg width="12" height="12" aria-label={m.name ?? undefined}>
                          <g transform="translate(6 6)">
                            <FacilityMark layer={m.layer} colour={m.inside ? INFRA_IN : INFRA_OUT} />
                          </g>
                        </svg>
                      </Marker>
                    ))}
                    {rows.map((place) => {
                      const atRisk = place.population_at_risk ?? 0
                      const r = 4 + 22 * Math.sqrt(Math.min(1, atRisk / 100000))
                      const colour = hazardColour(place.hazard_class)
                      return (
                        <Marker key={place.id} longitude={place.lon} latitude={place.lat} anchor="center" style={{ zIndex: 2, pointerEvents: 'none' }}>
                          <div className="relative" style={{ width: 2 * r, height: 2 * r }}>
                            <span
                              className="absolute inset-0 rounded-full"
                              style={{ background: colour, opacity: place.hazard_class ? 0.45 : 0.18, border: `1.5px solid ${colour}` }}
                            />
                            <span className="absolute left-1/2 top-1/2 h-1 w-1 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[var(--ink-text)]" />
                            <span
                              className="absolute top-1/2 -translate-y-1/2 whitespace-nowrap rounded-[var(--r-control)] bg-[var(--ink-bg)]/85 px-1 text-12 text-[var(--ink-text)]"
                              style={{ left: 2 * r + 6 }}
                            >
                              {place.name}
                            </span>
                          </div>
                        </Marker>
                      )
                    })}
                  </BaseMap>
                )}
              </div>
              <div
                className="flex shrink-0 flex-wrap items-center gap-3 border-t px-4 py-2"
                style={{ borderColor: 'var(--ink-border)' }}
              >
                <span className="section-label">Hazard</span>
                {[1, 2, 3, 4].map((c) => (
                  <span key={c} className="flex items-center gap-1" style={{ fontSize: 'var(--fs-label)', color: 'var(--ink-text-dim)' }}>
                    <span style={{ width: 10, height: 10, background: HAZARD[c], display: 'inline-block' }} />
                    {c}
                  </span>
                ))}
                {map?.present.has('roads') && (
                  <span className="flex items-center gap-1" style={{ fontSize: 'var(--fs-label)', color: 'var(--ink-text-dim)' }}>
                    <svg width="18" height="10">
                      <path d="M0 5H18" stroke={INFRA_IN} strokeWidth="1.5" />
                    </svg>
                    Road
                  </span>
                )}
                {map?.present.has('railways') && (
                  <span className="flex items-center gap-1" style={{ fontSize: 'var(--fs-label)', color: 'var(--ink-text-dim)' }}>
                    <svg width="18" height="10">
                      <path d="M0 5H18" stroke={INFRA_IN} strokeWidth="2" strokeDasharray="5 3" />
                    </svg>
                    Railway
                  </span>
                )}
                {map?.present.has('bridges') && (
                  <span className="flex items-center gap-1" style={{ fontSize: 'var(--fs-label)', color: 'var(--ink-text-dim)' }}>
                    <svg width="18" height="10">
                      <path d="M2 5H16" stroke="var(--ink-text)" strokeWidth="4" />
                    </svg>
                    Bridge
                  </span>
                )}
                {FACILITY_LEGEND.filter(([layer]) => map?.present.has(layer)).map(([layer, label]) => (
                  <span key={layer} className="flex items-center gap-1" style={{ fontSize: 'var(--fs-label)', color: 'var(--ink-text-dim)' }}>
                    <svg width="12" height="12">
                      <g transform="translate(6 6)">
                        <FacilityMark layer={layer} colour={INFRA_IN} />
                      </g>
                    </svg>
                    {label}
                  </span>
                ))}
                {map && map.present.size > 0 && (
                  <span style={{ fontSize: 'var(--fs-label)', color: 'var(--ink-text-dim)' }}>grey: outside the footprint</span>
                )}
              </div>
            </section>

          {/* TABLE · WARNING WINDOW */}
          <div
            className="grid gap-4"
            style={{ gridTemplateColumns: hasPlaces ? 'minmax(0, 1.6fr) minmax(0, 1fr)' : 'minmax(0, 1fr)' }}
          >
            {/* LEFT: settlement table */}
            {hasPlaces && (
            <section className="panel min-h-0 overflow-auto" style={{ padding: 0 }}>
              <div className="panel-header">
                <span className="panel-title" style={{ fontSize: 'var(--fs-body)', fontWeight: 600 }}>
                  Settlements
                </span>
                <span className="section-label">within {radiusLabel} of centre</span>
              </div>
              <table className="data-table">
                <thead>
                  <tr>
                    {COLUMNS.map(([key, label, numeric]) => (
                      <th
                        key={key}
                        onClick={() => setSort((s) => ({ key, desc: s.key === key ? !s.desc : key !== 'name' }))}
                        style={{ cursor: 'pointer', textAlign: numeric ? 'right' : 'left' }}
                        title={`Sort by ${label}`}
                      >
                        {label}
                        {sort.key === key ? (sort.desc ? ' ▾' : ' ▴') : ''}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((p) => (
                    <tr key={p.id}>
                      <td style={{ borderLeft: `3px solid ${hazardColour(p.hazard_class)}` }}>
                        <div style={{ color: 'var(--ink-text)' }}>{p.name}</div>
                        <div className="cell-note">
                          {int(Math.round(p.area_fraction_inundated * 100))}% of footprint
                          {p.population_at_risk === null ? '' : ` · ${int(p.population_at_risk)} at risk`}
                        </div>
                      </td>
                      <td className="num">{p.distance_from_dam_km === null ? '' : fmt(p.distance_from_dam_km, 1)}</td>
                      <td className="num">{p.population === null ? '' : int(p.population)}</td>
                      <td className="num">
                        {p.arrival_time_min === null ? '' : fmt(p.arrival_time_min / 60, 1)}
                      </td>
                      <td className="num">{p.max_depth_m === null ? '' : fmt(p.max_depth_m, 1)}</td>
                      <td className="num">{p.max_velocity_ms === null ? '' : fmt(p.max_velocity_ms, 2)}</td>
                      <td>
                        {p.hazard_class === null ? null : (
                          <span
                            className="hazard-chip"
                            style={{
                              border: `1px solid ${hazardColour(p.hazard_class)}`,
                              color: hazardColour(p.hazard_class),
                            }}
                            title={p.hazard_label ?? undefined}
                          >
                            {p.hazard_class}
                          </span>
                        )}
                        <div className="cell-note">
                          {p.duration_h === null ? '' : `${fmt(p.duration_h, 1)} h wet`}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>

            )}

            {/* RIGHT: arrival time / warning window */}
            {hasPlaces && (
            <section className="panel flex min-h-0 flex-col" style={{ padding: 0 }}>
              <div className="panel-header">
                <span className="panel-title" style={{ fontSize: 'var(--fs-body)', fontWeight: 600 }}>
                  Warning window
                </span>
                <span className="section-label">hours from breach</span>
              </div>
              <div ref={chartRef} className="flex min-h-0 flex-1 flex-col justify-center overflow-auto p-4">
                {chart &&
                  (() => {
                    const rowH = 56
                    const left = 76
                    const W = Math.max(240, chartBox.w - 32)
                    const H = chart.reached.length * rowH + 34
                    // The value label sits outside the bar, so the axis stops
                    // short of the right edge and the label still lands inside.
                    const plot = W - left - 52
                    const x = (h: number) => left + (h / chart.top) * plot
                    return (
                      <svg width={W} height={H}>
                        {chart.ticks.map((t) => (
                          <g key={t}>
                            <line
                              x1={x(t)}
                              y1="0"
                              x2={x(t)}
                              y2={H - 26}
                              stroke="var(--ink-border)"
                              strokeWidth="1"
                            />
                            <text
                              x={x(t)}
                              y={H - 10}
                              fontSize={fs(12)}
                              textAnchor="middle"
                              fill="var(--ink-text-dim)"
                              style={{ fontFamily: 'var(--font-mono)' }}
                            >
                              {t}
                            </text>
                          </g>
                        ))}
                        {chart.reached.map((p, i) => {
                          const h = (p.arrival_time_min ?? 0) / 60
                          const y = i * rowH + 14
                          return (
                            <g key={p.id}>
                              <text x={left - 8} y={y + 11} fontSize={fs(14)} textAnchor="end" fill="var(--ink-text)">
                                {p.name}
                              </text>
                              <rect
                                x={left}
                                y={y}
                                width={Math.max(2, x(h) - left)}
                                height="16"
                                fill={hazardColour(p.hazard_class)}
                                fillOpacity="0.75"
                              />
                              <text
                                x={x(h) + 6}
                                y={y + 13}
                                fontSize={fs(12)}
                                fill="var(--ink-text)"
                                style={{ fontFamily: 'var(--font-mono)' }}
                              >
                                {fmt(h, 1)} h
                              </text>
                              <text
                                x={left}
                                y={y + 32}
                                fontSize={fs(12)}
                                fill="var(--ink-text-dim)"
                                style={{ fontFamily: 'var(--font-mono)' }}
                              >
                                {clock(p.arrival_time_min ?? 0)} · {int(p.population_at_risk ?? 0)} at risk
                              </text>
                            </g>
                          )
                        })}
                      </svg>
                    )
                  })()}
                <div className="section-label mt-2 text-center">Hours after breach</div>
              </div>
            </section>
            )}
          </div>

          {/* BOTTOM: infrastructure inside the footprint, measured against the run's own
              wet area. A category with nothing inside does not come back from the API. */}
          {(data.infrastructure?.categories?.length ?? 0) > 0 && (
            <section className="panel shrink-0" style={{ padding: 0 }}>
              <div className="panel-header">
                <span className="panel-title" style={{ fontSize: 'var(--fs-body)', fontWeight: 600 }}>
                  Infrastructure in the footprint
                </span>
                <span className="section-label">
                  {data.infrastructure.attribution}
                  {data.infrastructure.fetched_utc ? ` · extract ${data.infrastructure.fetched_utc.slice(0, 10)}` : ''}
                </span>
              </div>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Category</th>
                    <th>Class</th>
                    <th style={{ textAlign: 'right' }}>Value</th>
                    <th>Measure</th>
                    <th>Named in the extract</th>
                  </tr>
                </thead>
                <tbody>
                  {data.infrastructure.categories.map((c) => {
                    const names = (c.names ?? []).filter(Boolean)
                    return (
                      <tr key={c.key}>
                        <td>{c.category}</td>
                        <td>{c.detail}</td>
                        <td className="num">{c.unit.startsWith('km') ? fmt(c.value, 1) : int(c.value)}</td>
                        <td>{c.unit}</td>
                        <td style={{ color: 'var(--ink-text-dim)' }}>
                          {names.slice(0, 4).join(', ')}
                          {names.length > 4 ? ` and ${int(names.length - 4)} more` : ''}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </section>
          )}
        </div>
      )}
    </div>
  )
}
